import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

export type LabelUsage = 'pending' | 'applied' | 'discarded' | 'unused'
export type LabelPrintKind = 'original' | 'reprint' | 'relabel'

/**
 * One physical label printed for a product (z8nrz7dt48), with the signatures
 * of its life: before printing, after printing (verified by scan) and on a
 * discard or return.
 */
export interface ProductLabel {
  id: string
  number: number
  productId: string
  submissionId: string
  procedureId: string | null
  din: string
  productCode: string
  labelType: string
  selections: Record<string, unknown> | null
  printKind: LabelPrintKind
  replacesId: string | null
  usage: LabelUsage
  printedAt: string
  printedBy: string
  printedByName: string | null
  preSignatureMeaning: string
  postVerifiedAt: string | null
  postVerifiedBy: string | null
  postVerifiedByName: string | null
  postSignatureMeaning: string | null
  scannedDin: string | null
  scannedProductCode: string | null
  usageAt: string | null
  usageBy: string | null
  usageByName: string | null
  usageSignatureMeaning: string | null
  usageReason: string | null
  /** Present on list and update responses. */
  product?: { patientMrn: string | null; patientName: string | null; procedureId: string | null }
  replaces?: { number: number } | null
}

/** A form's labels: issued = applied + discarded + unused, and what is still pending. */
export interface LabelReconciliation {
  issued: number
  applied: number
  discarded: number
  unused: number
  pending: number
  balanced: boolean
  pendingLabels: { id: string; number: number; din: string; productCode: string }[]
  /** The reconciliation signed when the form last completed (AC7); null until then. */
  recorded: RecordedLabelReconciliation | null
}

/** A form's label reconciliation as it was signed at completion — the counts may have moved since. */
export interface RecordedLabelReconciliation {
  issued: number
  applied: number
  discarded: number
  unused: number
  pending: number
  balanced: boolean
  summary: string
  /** Why it did not balance; null when it did. */
  discrepancy: string | null
  /** Numbers of the labels unaccounted for at completion. */
  unaccounted: number[]
  signedBy: string
  signedAt: string
}

export interface IssueLabelItem {
  productId: string
  labelType: string
  bloodType?: string
  specialMessage?: string
  intendedUse?: string
  donationType?: string
  freeText?: string[]
  printKind?: LabelPrintKind
  replacesId?: string
}

export interface LabelScan {
  din?: string
  productCode?: string
}

export interface LabelScanCheck {
  ok: boolean
  mismatches: string[]
  belongsTo: string | null
}

export interface ListLabelsQuery {
  productId?: string
  submissionId?: string
  procedureId?: string
  usage?: LabelUsage
  unverified?: boolean
  q?: string
  take?: number
}

/** Proof the signer re-entered their password (lib/signingToken.ts), and what the signature means. */
interface Signed {
  signingToken: string
  signatureMeaning: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const body = JSON.parse(text) as { message?: string | string[] }
      // The server's lines are full sentences already ("DIN: expected …, scanned ….").
      message = Array.isArray(body.message) ? body.message.join(' ') : (body.message ?? text)
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

function queryString(query: ListLabelsQuery): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '' && value !== false) params.set(key, String(value))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

// Tenant-scoped: authHeaders injects X-Tenant-Id for the selected tenant.
export const productLabelsApi = {
  list: (query: ListLabelsQuery = {}) => request<ProductLabel[]>(`/product-labels${queryString(query)}`),

  summary: (submissionId: string) =>
    request<LabelReconciliation>(`/product-labels/summary?submissionId=${encodeURIComponent(submissionId)}`),

  /** Pre-print verification: records and signs every label before any prints. */
  issue: (payload: Signed & { items: IssueLabelItem[] }) =>
    request<ProductLabel[]>('/product-labels', { method: 'POST', body: JSON.stringify(payload) }),

  check: (id: string, scan: LabelScan) =>
    request<LabelScanCheck>(`/product-labels/${encodeURIComponent(id)}/check`, {
      method: 'POST',
      body: JSON.stringify(scan),
    }),

  /** Post-print verification: the scan must match; signed, the label is applied. */
  verify: (id: string, payload: Signed & LabelScan) =>
    request<ProductLabel>(`/product-labels/${encodeURIComponent(id)}/verify`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  setUsage: (id: string, payload: Signed & { usage: 'discarded' | 'unused'; reason?: string }) =>
    request<ProductLabel>(`/product-labels/${encodeURIComponent(id)}/usage`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}
